import {
  useCreateDemoMutation,
  useGetRecordingQuery,
} from '@carboncopyco/data';
import {
  DemoIcon,
  DownOutlineIcon,
  ImportOutlineIcon,
  OpenInNew1OutlineIcon,
  ShowOutlineIcon,
} from '@carboncopyco/icons';
import {
  PermissionAction,
  PermissionKey,
  RecordingTypes,
} from '@carboncopyco/types';
import {
  Button,
  Dropdown,
  DropdownItem,
  Separator,
  notify,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ClonePageActionsDropdown } from '../../AssetActions/Clone/ClonePageActionsDropdown';
import {
  useDesktopDeeplink,
  usePermissionsContext,
  useSearchParamAction,
} from '../../utils';
import { useClonePageProps } from './ClonePageContext';

export const ClonePageActionButtons = () => {
  const {
    organizationId,
    cloneId,
    goToEditor,
    goToDemoPage,
    openImportEditsModal,
    spliceCloneLdFlag,
    handleClonePreview,
  } = useClonePageProps();
  const flags = useFlags();
  const { getPermissions } = usePermissionsContext();
  const { exeDeeplinkWithAction, shouldShowDeeplink, exeDeeplink } =
    useDesktopDeeplink();
  const [isHoverFromScratch, setHoverFromScratch] = useBoolean(false);
  const [isHoverImportEdits, setHoverImportEdits] = useBoolean(false);
  const createClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.createRecording
  );

  const duplicateClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.duplicateRecord
  );

  const copyToWorkspacePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.copyRecord
  );
  const deleteClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.deleteRecordingById
  );

  const continueCloningPermission = getPermissions(
    PermissionKey.draft,
    PermissionAction.createARecordingDraft
  );

  const { data: clone } = useGetRecordingQuery({
    organizationId,
    id: cloneId,
  });

  const [createDemo, { isLoading }] = useCreateDemoMutation();

  useSearchParamAction({
    param: 'importEdits',
    action: openImportEditsModal,
    expectedValue: 'true',
  });

  function onCreateDemo() {
    setHoverFromScratch.setFalse();
    const folderId = String(clone.folderId);
    createDemo({
      organizationId,
      recordingId: Number(cloneId),
      demoName: `Demo for ${clone.recordName}`,
      ...(folderId && folderId !== '0' ? { folderId } : {}),
    })
      .unwrap()
      .then((demo) =>
        exeDeeplinkWithAction(
          {
            action: 'edit',
            assetId: demo.id,
            organizationId: organizationId,
            workspaceId: demo.workspaceId,
            assetType: 'demo',
          },
          () => {
            return demo.recordingType === RecordingTypes.mobile &&
              !flags.editMobileCloneDemo
              ? goToDemoPage(demo.id)
              : goToEditor(demo.id, demo.recordingType);
          }
        )
      )
      .catch(() => notify.error('Error creating demo'));
  }

  return (
    <>
      <div className="flex items-center gap-5">
        <ClonePageActionsDropdown
          {...useClonePageProps()}
          asChild
          arrow
          align="center"
          triggerElement={
            (duplicateClonePermission ||
              copyToWorkspacePermission ||
              deleteClonePermission ||
              continueCloningPermission) && (
              <Button
                link
                blue
                rightIcon={<DownOutlineIcon />}
                label="Actions"
              />
            )
          }
        />
        <div className="h-7">
          <Separator />
        </div>
        {clone && (
          <Button
            secondary
            leftIcon={<ShowOutlineIcon />}
            label="Preview"
            onClick={() => handleClonePreview(clone)}
          />
        )}
        {createClonePermission &&
          clone &&
          (spliceCloneLdFlag ? (
            <Dropdown
              width={175}
              asChild
              arrow
              triggerElement={
                <Button
                  rightIcon={<DownOutlineIcon />}
                  label="Create demo"
                  disabled={isLoading}
                />
              }
            >
              <div
                onMouseEnter={setHoverFromScratch.setTrue}
                onMouseLeave={setHoverFromScratch.setFalse}
              >
                <DropdownItem
                  iconRight={
                    isHoverFromScratch && shouldShowDeeplink ? (
                      <OpenInNew1OutlineIcon size={15} />
                    ) : null
                  }
                  iconLeft={<DemoIcon />}
                  label="From scratch"
                  onClick={onCreateDemo}
                  disabled={isLoading}
                />
              </div>
              <div
                onMouseEnter={setHoverImportEdits.setTrue}
                onMouseLeave={setHoverImportEdits.setFalse}
              >
                <DropdownItem
                  iconRight={
                    isHoverImportEdits && shouldShowDeeplink ? (
                      <OpenInNew1OutlineIcon size={15} />
                    ) : null
                  }
                  iconLeft={<ImportOutlineIcon />}
                  label="Import edits"
                  onClick={() => {
                    setHoverImportEdits.setFalse();
                    exeDeeplink('importEdits', 'true', openImportEditsModal);
                  }}
                />
              </div>
            </Dropdown>
          ) : (
            <Button
              label="Create demo"
              onClick={onCreateDemo}
              disabled={isLoading}
              rightIcon={
                shouldShowDeeplink ? <OpenInNew1OutlineIcon size={15} /> : null
              }
            />
          ))}
      </div>
    </>
  );
};
